import React, { useState, useEffect, useRef } from 'react';
import API_BASE_URL from '../../config/api';

const ChatManager = () => {
    const [conversations, setConversations] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [reply, setReply] = useState('');
    const [loading, setLoading] = useState(false);

    const messagesEndRef = useRef(null);

    // Fetch Conversations
    const fetchConversations = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/chat/admin/conversations`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const json = await res.json();
            if (json.status === 'success') {
                setConversations(json.data.conversations);
            }
        } catch (err) {
            console.error("Failed to load conversations", err);
        }
    };

    useEffect(() => {
        fetchConversations();
        const interval = setInterval(fetchConversations, 10000); // Refresh list every 10s
        return () => clearInterval(interval);
    }, []);

    // Fetch Messages for selected chat
    useEffect(() => {
        if (!selectedChat) return;

        const fetchMessages = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_BASE_URL}/chat/${selectedChat._id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const json = await res.json();
                if (json.status === 'success') {
                    setMessages(json.data.messages);
                    scrollToBottom();
                }
            } catch (err) {
                console.error("Failed to load messages", err);
            }
        };

        fetchMessages();
        const interval = setInterval(fetchMessages, 3000); // Poll active chat every 3s
        return () => clearInterval(interval);
    }, [selectedChat]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleSendReply = async () => {
        if (!reply.trim() || !selectedChat) return;

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/chat/message`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    conversationId: selectedChat._id,
                    message: reply
                })
            });
            const json = await res.json();
            if (json.status === 'success') {
                setMessages(json.data.messages);
                setReply('');
                scrollToBottom();
            }
        } catch (err) {
            alert("Failed to send reply");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex h-[calc(100vh-100px)] bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Sidebar List */}
            <div className="w-1/3 border-r border-gray-200 bg-gray-50 flex flex-col">
                <div className="p-4 border-b border-gray-200 font-semibold text-gray-700">
                    Active Conversations
                </div>
                <div className="flex-1 overflow-y-auto">
                    {conversations.map(chat => (
                        <div
                            key={chat._id}
                            onClick={() => setSelectedChat(chat)}
                            className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-100 ${selectedChat?._id === chat._id ? 'bg-white border-l-4 border-l-black' : ''}`}
                        >
                            <div className="flex justify-between mb-1">
                                <span className="font-medium text-sm text-gray-900">
                                    {chat.user ? `${chat.user.firstName} ${chat.user.lastName}` : `Guest (${chat.guestId?.substr(0, 8)})`}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {new Date(chat.lastMessageAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 truncate">
                                {chat.messages[chat.messages.length - 1]?.content || "No messages"}
                            </p>
                        </div>
                    ))}
                    {conversations.length === 0 && (
                        <div className="p-4 text-center text-gray-500 text-sm">No conversations found</div>
                    )}
                </div>
            </div>

            {/* Chat Window */}
            <div className="flex-1 flex flex-col">
                {selectedChat ? (
                    <>
                        <div className="p-4 border-b border-gray-200 bg-white flex justify-between items-center">
                            <h3 className="font-semibold text-gray-800">
                                Chatting with {selectedChat.user ? selectedChat.user.firstName : 'Guest'}
                            </h3>
                            <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">Active</span>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                            {messages.map((msg, idx) => (
                                <div key={idx} className={`flex ${msg.role === 'admin' ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[70%] px-4 py-3 rounded-lg ${msg.role === 'admin'
                                            ? 'bg-black text-white'
                                            : msg.role === 'assistant'
                                                ? 'bg-gray-200 text-gray-800'
                                                : 'bg-white border border-gray-200 text-gray-800'
                                        }`}>
                                        <div className="text-xs opacity-70 mb-1 capitalize">{msg.role}</div>
                                        <p className="text-sm shadow-sm">{msg.content}</p>
                                    </div>
                                </div>
                            ))}
                            <div ref={messagesEndRef} />
                        </div>

                        <div className="p-4 border-t border-gray-200 bg-white">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={reply}
                                    onChange={(e) => setReply(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSendReply()}
                                    placeholder="Type a reply..."
                                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-black focus:border-transparent outline-none"
                                />
                                <button
                                    onClick={handleSendReply}
                                    disabled={loading || !reply.trim()}
                                    className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-50"
                                >
                                    Send
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-400">
                        Select a conversation to start chatting
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatManager;
